/**
 * Vivianna welcome newsletter modal — centered two-step popup.
 * Email submission uses Shopify customer form (swap data-vivianna-newsletter-form for Klaviyo later).
 */
class ViviannaNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this._onKeyDown = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.hide();
      }
    };

    this.querySelectorAll('[data-vivianna-newsletter-close]').forEach((button) => {
      button.addEventListener('click', () => this.hide());
    });

    this.querySelector('[data-vivianna-newsletter-overlay]')?.addEventListener('click', () => this.hide());

    this.querySelector('[data-vivianna-newsletter-copy]')?.addEventListener('click', () => this.copyCode());

    this.querySelector('[data-vivianna-newsletter-form]')?.addEventListener('submit', (event) => {
      this._handleFormSubmit(event);
    });

    document.addEventListener('keydown', this._onKeyDown);

    if (this.hasAttribute('open-on-load')) {
      this.show();
    } else if (this.shouldAppearAutomatically) {
      window.setTimeout(() => this.show(), this.apparitionDelay);
    }

    this._bindThemeEditorEvents();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeyDown);
    this._themeEditorAbort?.abort();
  }

  _bindThemeEditorEvents() {
    if (typeof Shopify === 'undefined' || !Shopify.designMode) {
      return;
    }

    const section = this.closest('.shopify-section');
    if (!section) {
      return;
    }

    this._themeEditorAbort = new AbortController();
    const { signal } = this._themeEditorAbort;

    if (this.hasAttribute('handle-section-events')) {
      section.addEventListener('shopify:section:select', () => this.show(), { signal });
      section.addEventListener('shopify:section:deselect', () => this.hide(), { signal });
    }
  }

  get apparitionDelay() {
    return parseInt(this.getAttribute('apparition-delay') || '5', 10) * 1000;
  }

  get shouldAppearAutomatically() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      return false;
    }

    return !(
      window.localStorage.getItem('theme:popup-filled') === 'true' ||
      (this.hasAttribute('only-once') && window.localStorage.getItem('theme:popup-appeared') === 'true')
    );
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  show() {
    if (this.isOpen) {
      return;
    }

    this.setAttribute('open', '');
    document.documentElement.classList.add('lock');
    window.localStorage.setItem('theme:popup-appeared', 'true');

    const emailInput = this.querySelector('[data-vivianna-newsletter-step="email"] input[type="email"]');
    if (emailInput) {
      window.setTimeout(() => emailInput.focus(), 150);
    }
  }

  hide() {
    if (!this.isOpen) {
      return;
    }

    this.removeAttribute('open');
    document.documentElement.classList.remove('lock');
  }

  async _handleFormSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const submitButton = form.querySelector('[data-vivianna-newsletter-submit]');
    const errorNode = form.querySelector('[data-vivianna-newsletter-error]');

    if (!this._hasValidEmail(form)) {
      this._showFormError(errorNode, (/^en\b/i.test(document.documentElement.lang || '') ? 'Please enter a valid email address.' : 'Bitte gib eine gültige E-Mail-Adresse ein.'));
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
    }

    this._hideFormError(errorNode);

    try {
      await this._runShopifyCaptcha(form);
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const html = await response.text();
      const succeeded = this._wasSubscribeSuccessful(response, html);

      if (succeeded) {
        this._showSuccessStep();
        return;
      }

      const serverError = this._extractFormError(html);
      if (serverError) {
        this._showFormError(errorNode, serverError);
        return;
      }

      if (this._isLocalDev()) {
        this._showSuccessStep({ devFallback: true });
        return;
      }

      this._showFormError(errorNode, (/^en\b/i.test(document.documentElement.lang || '') ? 'Sign-up failed. Please try again.' : 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.'));
    } catch (error) {
      if (this._isLocalDev()) {
        this._showSuccessStep({ devFallback: true });
      } else {
        this._showFormError(errorNode, (/^en\b/i.test(document.documentElement.lang || '') ? 'Sign-up failed. Please try again.' : 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.'));
      }
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  }

  _runShopifyCaptcha(form) {
    const captchaPromise = new Promise((resolve) => {
      if (window.Shopify?.captcha?.protect) {
        window.Shopify.captcha.protect(form, () => resolve());
        return;
      }

      resolve();
    });

    const timeoutPromise = new Promise((resolve) => {
      window.setTimeout(resolve, 12000);
    });

    return Promise.race([captchaPromise, timeoutPromise]);
  }

  _wasSubscribeSuccessful(response, html) {
    if (!response || !html) {
      return false;
    }

    const successMarker = this.getAttribute('data-success-marker');
    const successStepVisible = /data-vivianna-newsletter-step="success"[^>]*>(?![\s\S]*hidden)/i.test(html);
    const hasSuccessCopy = successMarker ? html.includes(successMarker) : false;
    const hasSuccessStep = html.includes('vivianna-newsletter-popup__step--success') && !/vivianna-newsletter-popup__step--success[^>]*hidden/i.test(html);

    if (hasSuccessCopy || hasSuccessStep || successStepVisible) {
      return true;
    }

    if (response.ok && !this._extractFormError(html)) {
      return true;
    }

    return false;
  }

  _extractFormError(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const errorNode = doc.querySelector('[data-vivianna-newsletter-error]');

    if (errorNode && errorNode.textContent.trim()) {
      return errorNode.textContent.trim();
    }

    const genericError = doc.querySelector('.form__message--error, .errors');
    if (genericError && genericError.textContent.trim()) {
      return genericError.textContent.trim();
    }

    return '';
  }

  _hasValidEmail(form) {
    const emailInput = form.querySelector('input[type="email"]');
    const value = emailInput?.value?.trim() || '';
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  _isLocalDev() {
    return ['127.0.0.1', 'localhost'].includes(window.location.hostname);
  }

  _showSuccessStep({ devFallback = false } = {}) {
    const emailStep = this.querySelector('[data-vivianna-newsletter-step="email"]');
    const successStep = this.querySelector('[data-vivianna-newsletter-step="success"]');

    emailStep?.setAttribute('hidden', '');
    successStep?.removeAttribute('hidden');

    window.localStorage.setItem('theme:popup-filled', 'true');
    this.show();

    if (devFallback) {
      console.info('[vivianna-newsletter-popup] Local preview: showing discount step without confirmed Shopify signup.');
    }
  }

  _showFormError(errorNode, message) {
    if (!errorNode) {
      return;
    }

    errorNode.textContent = message;
    errorNode.removeAttribute('hidden');
  }

  _hideFormError(errorNode) {
    if (!errorNode) {
      return;
    }

    errorNode.textContent = '';
    errorNode.setAttribute('hidden', '');
  }

  copyCode() {
    const code = this.querySelector('[data-vivianna-newsletter-code]')?.textContent?.trim();
    if (!code) {
      return;
    }

    const copyButton = this.querySelector('[data-vivianna-newsletter-copy]');
    const copiedText = copyButton?.getAttribute('data-copied-text') || 'Kopiert!';

    const onCopied = () => {
      if (!copyButton) {
        return;
      }

      copyButton.classList.add('is-copied');
      const label = copyButton.querySelector('[data-copy-label]');
      if (label) {
        const original = label.textContent;
        label.textContent = copiedText;
        window.setTimeout(() => {
          label.textContent = original;
          copyButton.classList.remove('is-copied');
        }, 2000);
      }
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(onCopied).catch(() => {
        this._fallbackCopy(code, onCopied);
      });
    } else {
      this._fallbackCopy(code, onCopied);
    }
  }

  _fallbackCopy(code, onCopied) {
    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      onCopied();
    } catch (error) {
      // ignore
    }

    textarea.remove();
  }
}

if (!window.customElements.get('vivianna-newsletter-popup')) {
  window.customElements.define('vivianna-newsletter-popup', ViviannaNewsletterPopup);
}
